import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogApi } from '../api';
import { CustomerContext } from '../contexts/CustomerContext';
import type { Catalog } from '../types';
import { Header } from '../components/Header';
import { CategorySection } from '../components/CategorySection';
import { Loading } from '../components/Loading';
import { useCart } from '../hooks/useCart';
import { useTelegram } from '../hooks/useTelegram';
import './CatalogPage.css';

export const CatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const customer = useContext(CustomerContext);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { addToCart, updateCartItem, cart: updatedCart } = useCart();
  const { tg } = useTelegram();

   
  useEffect(() => {
    loadCatalog();
  }, []);

  // Update only cart in catalog when cart changes (no full page reload)
  useEffect(() => {
    if (updatedCart && catalog) {
      setCatalog(prev => prev ? { ...prev, cart: updatedCart } : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedCart]);

  const loadCatalog = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await catalogApi.getCatalog();
      setCatalog(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить каталог');
      console.error('Failed to load catalog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId: number) => {
    const success = await addToCart(productId, 1);

    if (success) {
      // Show haptic feedback if available
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }
      // Cart will be updated automatically via useEffect watching updatedCart
    } else {
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    }
  };

  const handleUpdateQuantity = async (itemId: number, quantity: number) => {
    const success = await updateCartItem(itemId, quantity);

    if (success) {
      // Show haptic feedback if available
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.selectionChanged();
      }
      // Cart will be updated automatically via useEffect watching updatedCart
    } else {
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  const handleCartClick = () => {
    navigate('/cart');
  };

  const handleProfileClick = () => {
    navigate('/orders');
  };

  const handleAdminClick = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="page">
        <Header
          onProfileClick={handleProfileClick}
          onAdminClick={handleAdminClick}
          isAdmin={customer?.is_admin}
        />
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <Header
          onProfileClick={handleProfileClick}
          onAdminClick={handleAdminClick}
          isAdmin={customer?.is_admin}
        />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={loadCatalog}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="page">
        <Header
          onProfileClick={handleProfileClick}
          onAdminClick={handleAdminClick}
          isAdmin={customer?.is_admin}
        />
        <div className="empty-container">
          <p className="empty-message">Каталог пуст</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page catalog-page">
      <Header
        cartItemsCount={catalog.cart.items?.length || 0}
        onCartClick={handleCartClick}
        onProfileClick={handleProfileClick}
        onAdminClick={handleAdminClick}
        isAdmin={customer?.is_admin}
      />

      <main className="catalog-content">
        {catalog.categories.length === 0 ? (
          <div className="empty-container">
            <p className="empty-message">Товары не найдены</p>
          </div>
        ) : (
          catalog.categories.map((category) => (
            <CategorySection
              key={category.id}
              category={category}
              cartItems={catalog.cart.items || []}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={handleUpdateQuantity}
              onProductClick={handleProductClick}
            />
          ))
        )}
      </main>
    </div>
  );
};
